import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ResolveEnd,
  ResolveStart,
  Router,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
} from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Loader global bloqueante. Dos fuentes alimentan el mismo contador `pending`:
 * los eventos del Router (navegación/lazy-load/resolvers) y llamadas manuales
 * `show()`/`hide()` desde componentes que necesitan cubrir trabajo async
 * (p. ej. recarga de webconfig/permisos) que ocurre *antes* de navegar.
 */
@Injectable({
  providedIn: 'root',
})
export class NavLoaderService {
  private readonly router = inject(Router);

  private readonly pending = signal(0);
  readonly loading = computed(() => this.pending() > 0);
  readonly message = signal<string | null>(null);

  constructor() {
    this.router.events
      .pipe(
        filter(
          (e: Event) =>
            e instanceof NavigationStart ||
            e instanceof RouteConfigLoadStart ||
            e instanceof ResolveStart ||
            e instanceof NavigationEnd ||
            e instanceof RouteConfigLoadEnd ||
            e instanceof ResolveEnd ||
            e instanceof NavigationCancel ||
            e instanceof NavigationError,
        ),
      )
      .subscribe((e) => {
        if (
          e instanceof NavigationStart ||
          e instanceof RouteConfigLoadStart ||
          e instanceof ResolveStart
        ) {
          this.pending.set(this.pending() + 1);
        } else {
          // End/cancel/error cierran uno
          this.pending.set(Math.max(0, this.pending() - 1));
        }
      });
  }

  show(message?: string): void {
    this.pending.set(this.pending() + 1);
    if (message) this.message.set(message);
  }

  hide(): void {
    const next = Math.max(0, this.pending() - 1);
    this.pending.set(next);
    if (next === 0) this.message.set(null);
  }
}
